import type {
    PresentationSelectOption,
    PresentationSelectPlacement
} from '../events/PresentationSelect';
import { PresentationSelect } from '../events/PresentationSelect';
import { OPEN_STATUS_OPTIONS, type OpenVirtualAppointmentStatus } from './openVirtualAppointments';
import { VIRTUAL_APPOINTMENT_STATUS_LABELS } from '@odo/shared-ui/clinical/virtualAppointments/summary';
import { virtualAppointmentStatusBg } from './virtualAppointmentStatusColors';

// On the solid trigger the dot fades to white, as in SolidStatusPill; in the list it wears the colour.
function StatusDot({ bg, placement }: { bg: string; placement: PresentationSelectPlacement }) {
    const tone = placement === 'trigger' ? 'bg-white/40' : bg;
    return <span aria-hidden="true" className={`w-2 h-2 rounded-full flex-shrink-0 ${tone}`} />;
}

const STATUS_OPTIONS: readonly PresentationSelectOption<OpenVirtualAppointmentStatus>[] =
    OPEN_STATUS_OPTIONS.map((status) => {
        const bg = virtualAppointmentStatusBg(status);
        return {
            value: status,
            label: VIRTUAL_APPOINTMENT_STATUS_LABELS[status],
            render: (placement) => <StatusDot bg={bg} placement={placement} />,
            triggerTone: {
                trigger: `border-transparent ${bg} text-white font-semibold hover:brightness-95 disabled:hover:brightness-100`,
                chevron: 'text-white/80'
            }
        };
    });

interface VirtualAppointmentStatusDropdownProps {
    id: string;
    value: OpenVirtualAppointmentStatus;
    onChange: (status: OpenVirtualAppointmentStatus) => void;
    disabled?: boolean;
}

// Picks between the working statuses of an open VA; done and snooze are separate actions.
export function VirtualAppointmentStatusDropdown({
    id,
    value,
    onChange,
    disabled = false
}: VirtualAppointmentStatusDropdownProps) {
    const select = (next: OpenVirtualAppointmentStatus) => {
        if (next !== value) onChange(next);
    };
    return (
        <PresentationSelect
            id={id}
            value={value}
            options={STATUS_OPTIONS}
            onChange={select}
            disabled={disabled}
            size="sm"
        />
    );
}
